import { useState } from 'react'
import type { UseQueryResult } from '@tanstack/react-query'
import { KakaoPointMap } from '@/shared/ui/KakaoPointMap'
import { QueryState } from '@/shared/ui/QueryState'
import type { OrgDetail } from '@/shared/lib/apiTypes'
import { DefaultLayout } from '@/shared/ui/DefaultLayout'
import { LocationPinIcon } from '@/shared/ui/icons/LocationPinIcon'
import { InfoIcon } from '@/shared/ui/icons/InfoIcon'
import { CallBellIcon } from '@/shared/ui/icons/CallBellIcon'
import { UserIcon } from '@/shared/ui/icons/UserIcon'
import { MoneyIcon } from '@/shared/ui/icons/MoneyIcon'
import { ChevronDownIcon } from '@/shared/ui/icons/ChevronDownIcon'
import { ChevronUpIcon } from '@/shared/ui/icons/ChevronUpIcon'
import { useCommonCodes, makeLabeler } from '@/shared/lib/codes'
import { useSolutionCardLabels, type SolutionCardLabels } from '@/shared/lib/cardLabels'
import { DetailChipRow, DetailInlineRow } from '@/shared/ui/DetailRows'
import { GrayChip } from '@/shared/ui/GrayChip'

export function OrgDetailMobile({ query }: { query: UseQueryResult<OrgDetail> }) {
  const [infoOpen, setInfoOpen] = useState(false)
  const orgTypes = useCommonCodes('org_type')
  const orgTypeLabel = makeLabeler(orgTypes.data)
  const cardLabels = useSolutionCardLabels()

  const o = query.data
  const isReady = orgTypes.data !== undefined && cardLabels.isReady

  return (
    <DefaultLayout mobileTitle="등록 기업" className="pc:hidden flex min-h-[100dvh] flex-col">

      <QueryState isPending={query.isPending || !isReady} error={query.error}>
        {o ? (
          <>
            <article className="layout-body flex shrink-0 flex-col gap-[18px] p-4">
              <div className="flex items-start gap-3">
                <span
                  className="flex h-[100px] w-[100px] shrink-0 items-center justify-center overflow-hidden rounded-xl bg-violet-100"
                  aria-hidden="true"
                >
                  {o.logoUrl ? (
                    <img className="h-full w-full object-cover" src={o.logoUrl} alt="" />
                  ) : null}
                </span>
                <div className="flex min-w-0 flex-1 flex-col gap-3">
                  <GrayChip className="w-fit">{orgTypeLabel(o.orgType)}</GrayChip>
                  <div className="flex flex-col gap-2">
                    <p className="m-0 text-headline-xs leading-[1.4] font-semibold tracking-[-0.1px] text-black-800">
                      {o.name}
                    </p>
                    {o.baseRegionCode !== null && o.baseRegionCode.length > 0 ? (
                      <p className="m-0 flex items-center gap-1 text-body-md text-black-500">
                        <LocationPinIcon size={20} />
                        <span>{cardLabels.dongLabel(o.baseRegionCode)}</span>
                      </p>
                    ) : null}
                  </div>
                </div>
              </div>

              <button
                type="button"
                className="ui-pressable flex w-full items-center justify-center gap-1.5 rounded-xl border-0 bg-white-100 py-3 text-body-md font-normal text-black-100"
                aria-expanded={infoOpen}
                onClick={() => setInfoOpen((v) => !v)}
              >
                기업 정보 상세보기
                {infoOpen ? <ChevronUpIcon size={22} /> : <ChevronDownIcon size={22} />}
              </button>

              {infoOpen && (
                <div className="flex flex-col gap-3.5">
                  <LabeledText label="기업·기관 소개" text={o.intro} />
                  <LabeledText label="기업 미션" text={o.mission} />
                  <LabeledText label="기업 비전" text={o.vision} />

                  {o.homepageUrl ? (
                    <a
                      className="text-body-sm font-medium text-[#3f68e3] [overflow-wrap:anywhere]"
                      href={o.homepageUrl}
                      target="_blank"
                      rel="noreferrer noopener"
                    >
                      {o.homepageUrl}
                    </a>
                  ) : null}

                  <div className="flex flex-col gap-[3px]">
                    <p className="m-0 text-label font-medium text-black-100">위치</p>
                    <OrgMap location={o.location} />
                  </div>
                </div>
              )}
            </article>

            <div className="h-2 bg-violet-50 md:hidden" aria-hidden="true" />

            <SolutionCards cards={o.solutionCards} labels={cardLabels} />
          </>
        ) : null}
      </QueryState>
    </DefaultLayout>
  )
}

function OrgMap({ location }: { location: OrgDetail['location'] }) {
  if (location === null) return <div className="flex min-h-[140px] flex-1 shrink-0 grow-0 basis-auto overflow-hidden rounded-[14px] border border-white-200 bg-white-50 [&_.map-canvas]:h-auto [&_.map-canvas]:flex-auto [&_.map-canvas]:rounded-[inherit] [&_.map-canvas]:border-0" />

  return <KakaoPointMap lat={location.lat} lng={location.lng} className="flex min-h-[140px] flex-1 shrink-0 grow-0 basis-auto overflow-hidden rounded-[14px] border border-white-200 bg-white-50 [&_.map-canvas]:h-auto [&_.map-canvas]:flex-auto [&_.map-canvas]:rounded-[inherit] [&_.map-canvas]:border-0" />
}

function LabeledText({ label, text }: { label: string; text: string | null }) {
  if (!text) return null

  return (
    <div className="flex flex-col gap-[3px]">
      <p className="m-0 text-label font-medium text-black-100">{label}</p>
      <p className="m-0 text-body-md-reading whitespace-pre-wrap text-black-800">{text}</p>
    </div>
  )
}

export type OrgSolutionCardItem = OrgDetail['solutionCards'][number]

function SolutionCards({ cards, labels }: { cards: OrgSolutionCardItem[]; labels: SolutionCardLabels }) {
  if (cards.length === 0) return null

  return (
    <section className="layout-body flex flex-col gap-3.5 p-4">
      <div className="flex items-center gap-2">
        <h2 className="m-0 text-body-lg leading-[1.4] font-semibold text-black-600">기업의 해결 카드</h2>
        <span className="inline-flex items-center justify-center rounded-lg border border-[rgb(0_0_0/5%)] bg-yellow-50 px-3 py-[3px] text-body-sm font-medium whitespace-nowrap text-yellow-800">
          총 {cards.length}장
        </span>
      </div>

      <ul className="m-0 flex list-none flex-col gap-4 p-0">
        {cards.map((card) => (
          <li key={card.id}>
            <SolutionCardItem card={card} labels={labels} />
          </li>
        ))}
      </ul>
    </section>
  )
}

export function SolutionCardItem({ card, labels }: { card: OrgSolutionCardItem; labels: SolutionCardLabels }) {
  const [open, setOpen] = useState(false)

  return (
    <article className="flex flex-col gap-5 rounded-2xl border border-white-200 bg-white-50 px-[25px] py-[23px] shadow-[0_0_4px_-1px_rgb(0_0_0/8%),0_0_12px_-4px_rgb(0_0_0/16%)]">
      <div className="flex flex-col gap-2">
        {card.categoryCodes.length > 0 && (
          <div className="flex flex-wrap items-center gap-2">
            {card.categoryCodes.map((code) => (
              <span
                key={code}
                className="inline-flex shrink-0 items-center justify-center rounded-lg border border-[rgb(0_0_0/5%)] bg-white-200 px-3 py-1 text-body-sm font-medium whitespace-nowrap text-black-300"
              >
                {labels.categoryLabel(code)}
              </span>
            ))}
          </div>
        )}
        <p className="m-0 text-body-lg-reading font-semibold text-black-800">{card.title}</p>
        <p className="m-0 line-clamp-3 min-h-[5.1em] text-body-md-reading text-black-800">
          {card.summary}
        </p>
      </div>

      <span className="flex min-h-5 items-center gap-1 text-body-sm font-normal text-violet-600">
        {card.accessCondition ? (
          <>
            <InfoIcon size={20} />
            <span>{card.accessCondition}</span>
          </>
        ) : null}
      </span>

      <div className={`bg-white-100 pt-1 ${open ? 'rounded-2xl' : 'rounded-xl'}`}>
        <button
          type="button"
          className="ui-pressable flex w-full items-center justify-center gap-1.5 rounded-[10px] border-0 bg-transparent py-2 text-body-md font-normal text-black-100"
          aria-expanded={open}
          onClick={() => setOpen((v) => !v)}
        >
          상세보기
          {open ? <ChevronUpIcon size={22} /> : <ChevronDownIcon size={22} />}
        </button>

        {open && (
          <div className="flex flex-col gap-4 px-5 pt-1.5 pb-[18px]">
            <div className="flex flex-col gap-6">
              <DetailChipRow
                icon={<CallBellIcon size={20} />}
                label="제공 서비스"
                values={(card.resourceCodes ?? []).map(labels.resourceLabel)}
              />
              <DetailChipRow
                icon={<UserIcon size={20} />}
                label="해결 대상"
                values={(card.targetCodes ?? []).map(labels.targetLabel)}
              />
            </div>
            <div className="flex flex-col gap-3.5">
              <DetailInlineRow
                valueClass="text-label"
                icon={<LocationPinIcon size={20} />}
                label="지역"
                values={
                  card.serviceDongCodes != null && card.serviceDongCodes.length > 0
                    ? card.serviceDongCodes.map(labels.dongLabel)
                    : card.serviceAreaCodes.map(labels.serviceAreaLabel)
                }
              />
              <DetailInlineRow
                valueClass="text-label"
                icon={<MoneyIcon size={20} />}
                label="비용"
                values={
                  card.costType !== null && card.costType !== undefined
                    ? [labels.costTypeLabel(card.costType)]
                    : []
                }
              />
            </div>
          </div>
        )}
      </div>
    </article>
  )
}
